import { useNavigate } from "react-router-dom";
import { CheckCircle2, Circle, X, MessageSquare, UserPlus, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useProfile } from "@/hooks/useProfile";
import { useLeads } from "@/hooks/useLeads";

/**
 * 3-step activation card shown on the Dashboard for owners until they either
 * complete every step OR explicitly dismiss it. Persists dismissal to
 * `profiles.onboarding_dismissed_at` so it never re-appears across devices.
 *
 * Steps deliberately stay short and binary so the user can see exactly
 * "what's left" instead of feeling lost in a long onboarding flow.
 */
export default function ActivationChecklist() {
  const navigate = useNavigate();
  const { profile, updateProfile } = useProfile();
  const { leads } = useLeads();

  const profileComplete =
    !!profile?.agency_name && !!profile?.owner_name && !!(profile as any)?.segment;
  const widgetInstalled =
    Array.isArray((profile as any)?.service_zips) &&
    ((profile as any).service_zips as string[]).length > 0;
  const firstLead = leads.length > 0;

  const steps = [
    {
      key: "profile",
      title: "Complete your agency profile",
      desc: "Name, segment, contact info — takes about a minute.",
      done: profileComplete,
      icon: Building2,
      cta: "Open profile",
      onClick: () => navigate("/settings"),
    },
    {
      key: "widget",
      title: "Install your chat widget",
      desc: "Drop one snippet into your website so Sarah can capture leads 24/7.",
      done: widgetInstalled,
      icon: MessageSquare,
      cta: "Get the snippet",
      onClick: () => navigate("/embed"),
    },
    {
      key: "lead",
      title: "Send your first lead through",
      desc: "Try the chatbot or add a manual lead to see scoring in action.",
      done: firstLead,
      icon: UserPlus,
      cta: "Add a test lead",
      onClick: () => navigate("/quick-intake"),
    },
  ];

  const completed = steps.filter(s => s.done).length;
  const dismissed = !!(profile as any)?.onboarding_dismissed_at;

  // Don't render once everything is done OR the user explicitly closed it.
  if (dismissed || completed === steps.length) return null;

  async function dismiss() {
    try {
      await updateProfile({ onboarding_dismissed_at: new Date().toISOString() } as any);
    } catch {
      // Non-blocking — if the profile write fails the card just reappears next reload.
    }
  }

  return (
    <div className="rounded-xl border border-gold/30 bg-gold/5 p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="font-bold text-navy text-[15px]">Get started — {completed} of {steps.length} complete</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Finish these to start capturing leads tonight.
          </p>
        </div>
        <button
          onClick={dismiss}
          aria-label="Dismiss activation checklist"
          className="text-muted-foreground hover:text-foreground p-1"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <ul className="space-y-2">
        {steps.map(s => (
          <li
            key={s.key}
            className={`flex items-center gap-3 rounded-lg border bg-card px-3 py-2.5 ${
              s.done ? "opacity-70" : ""
            }`}
          >
            {s.done ? (
              <CheckCircle2 className="h-5 w-5 text-success shrink-0" />
            ) : (
              <Circle className="h-5 w-5 text-muted-foreground shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className={`text-sm font-medium ${s.done ? "line-through text-muted-foreground" : "text-navy"}`}>
                {s.title}
              </div>
              <div className="text-xs text-muted-foreground">{s.desc}</div>
            </div>
            {!s.done && (
              <Button size="sm" variant="outline" onClick={s.onClick}>
                {s.cta}
              </Button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}